import { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import TableDropdown, { TableDropdownOption } from 'app/shared-components/TableDropdown/TableDropdown';
import { listErpItems, type ErpPostingAccount } from 'app/services/erp-connection.service';

/**
 * SEE-1135 — per-transaction billable item picker for the coding grid. Generic
 * (erp*-named) so a future ERP can reuse it; the gate that surfaces the column is
 * Sage-specific today but the dropdown itself is ERP-neutral.
 *
 * The item catalog is the SAME for every row (the paidol's synced active items), so
 * listErpItems is cached + deduped per paidol in erp-connection.service (like
 * getErpPostingAccount) — every row's dropdown mounts and calls it, but only one
 * network request fires. The selected value comes straight off the transaction
 * (erpBillableItemId + the denormalized erpBillableItemName), so no lookup is needed
 * to show a label.
 */

/** "Name - (ITEMID)" when a distinct name exists, else just the id (mirrors the
 *  settings side sheet + the other coding dropdowns so duplicate names stay
 *  distinguishable). */
function itemLabel(item: ErpPostingAccount): string {
  return item.accountName && item.accountName !== item.accountId
    ? `${item.accountName} - (${item.accountId})`
    : item.accountId;
}

export interface ErpBillableItemDropdownProps {
  selectedItemId?: string | null;
  selectedItemName?: string | null;
  onValueChange: (option: ErpPostingAccount | null) => void;
  disabled?: boolean;
  enabled?: boolean;
  disableHeader?: boolean;
  label?: string;
  // SEE-1390: per-transaction billable flip. Shown (as a checkbox under the
  // dropdown) only when an item is selected AND onBillableChange is provided.
  // null/undefined billable renders checked — legacy item-is-the-signal rows
  // are billable until explicitly flipped off.
  billable?: boolean | null;
  onBillableChange?: (billable: boolean) => void;
}

const ErpBillableItemDropdown = ({
  selectedItemId,
  selectedItemName,
  onValueChange,
  disabled,
  enabled = true,
  disableHeader = true,
  label,
  billable,
  onBillableChange,
}: ErpBillableItemDropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const { selectedPaidol: paidol } = useAppSelector(selectUserCompanies);
  const paidolId = paidol?.id;
  const erpConnectionId = paidol?.erpConnectionId;

  const [items, setItems] = useState<ErpPostingAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!paidolId) return;
    let cancelled = false;
    setLoading(true);
    // Cached + deduped in the service — N row dropdowns share one fetch.
    listErpItems(paidolId, erpConnectionId)
      .then((result) => {
        if (!cancelled) setItems(result);
      })
      .catch((error) => {
        if (!cancelled) captureException(error, { extra: { paidolId } });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paidolId, erpConnectionId]);

  const options: TableDropdownOption<ErpPostingAccount>[] = useMemo(() => {
    const base = items.map((item) => ({ id: item.accountId, label: itemLabel(item), value: item }));
    // The saved item may not be in the synced list (deactivated in Sage, or list
    // still loading) — surface it from the row's denormalized fields so the cell
    // always shows what's coded, like CodingEntityDropdown's selectedOption fallback.
    if (selectedItemId && !base.some((o) => o.id === selectedItemId)) {
      const seed: ErpPostingAccount = {
        accountId: selectedItemId,
        accountName: selectedItemName ?? selectedItemId,
      };
      return [{ id: seed.accountId, label: itemLabel(seed), value: seed }, ...base];
    }
    return base;
  }, [items, selectedItemId, selectedItemName]);

  const selectedValue = useMemo(
    () => options.find((o) => o.id === selectedItemId)?.value ?? null,
    [options, selectedItemId]
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      {!disableHeader && (
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {label || t('billableItem.label', 'Item')}
        </Typography>
      )}
      <TableDropdown
        options={options}
        value={selectedValue}
        onSelect={(option) => onValueChange((option?.value as ErpPostingAccount) ?? null)}
        disabled={disabled || !enabled}
        loading={loading}
        placeholder={t('tableDropdown.unassigned', 'Unassigned')}
        searchable
        searchPlaceholder={t('tableDropdown.typeToSearch', 'Type to search...')}
        data-dropdown-type="erp-billable-item"
        data-dropdown-type-input="erp-billable-item-input"
      />
      {selectedItemId && onBillableChange && (
        <FormControlLabel
          sx={{ mt: 0.25, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
          control={
            <Checkbox
              size="small"
              sx={{ py: 0.25 }}
              // null/undefined = legacy item-is-the-signal: billable until flipped off.
              checked={billable !== false}
              onChange={(e) => onBillableChange(e.target.checked)}
              disabled={disabled || !enabled}
              inputProps={{ 'aria-label': t('billableItem.billable', 'Billable') }}
            />
          }
          label={t('billableItem.billable', 'Billable')}
        />
      )}
    </Box>
  );
};

export default ErpBillableItemDropdown;
